import { HttpClient } from '@angular/common/http';
import {
  Component,
  OnInit,
  Input,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { RfqService } from '@app/core/quotes';
import { RfqConversionRulesService } from '@app/core/quotes/services/rfq-conversion-rules.service';
import { UnitConversionRuleService } from '@app/core/unit-measurement/services/unit-conversion-rule/unit-conversion-rule.service';
import { environment } from '@app/env-service';
import { UnitConversionRuleType } from '@app/modules/unit-measurement/interfaces/unit-measurement.interface';

import { UpdateAlternateSkusComponent } from '../update-alternate-skus/update-alternate-skus.component';

@Component({
  selector: 'rfq-unit-of-conversions-popup-form',
  templateUrl: './rfq-unit-of-conversions-popup-form.component.html',
  styleUrls: ['./rfq-unit-of-conversions-popup-form.component.scss'],
})
export class RfqUnitOfConversionsPopupFormComponent implements OnInit, OnDestroy {
  url :string = environment.url;
  formData :UntypedFormGroup;
  pageTitle :string = 'Add New Search Filter Template';
  submitted :boolean = false;

  loading :boolean;

  filter :any = {};

  @Input() rfqConversionRule :any;
  @Input() rfq :any;

  alternateSKUs :any = [];
  unitOfMeasures :any = [];
  conversionRuleType = UnitConversionRuleType;

  baseUnit :any = {};

  private onDestroy$ = new Subject();

  constructor(
    public activeModal :NgbActiveModal,
    private fb :UntypedFormBuilder,
    private modalSvc :NgbModal,
    public rfqConversionRulesService :RfqConversionRulesService,
    private unitConversionRuleService :UnitConversionRuleService,
    private toastr :ToastrService,
    private http :HttpClient,
    private cd :ChangeDetectorRef,
    private rfqSvc :RfqService,
  ) {}

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  ngOnInit() :void {
    this.initUnitOfMeasures();
    this.getAlternateSKU();

    this.formData = this.fb.group({
      convertedUnitOfMeasureId: [null, Validators.required],
      conversionFactor: [null, Validators.required],
      baseUnitOfMeasureId: [this.baseUnit?.id, Validators.required],
      kitId: [null],
      rfqId: [this.rfq?.id, Validators.required],
      type: [UnitConversionRuleType.SKU, Validators.required],
      alternateSkus: [[]],
    });

    if (this.rfqConversionRule) {
      this.formData.patchValue({
        convertedUnitOfMeasureId: this.rfqConversionRule.convertedUnitOfMeasureId,
      });
      this.formData.patchValue({
        conversionFactor: this.rfqConversionRule.conversionFactor,
      });

      let alternate_sku_ids :any = [];
      if (
        this.rfqConversionRule.rfqAlternateSkus &&
        this.rfqConversionRule.rfqAlternateSkus.length > 0
      ) {
        alternate_sku_ids = this.rfqConversionRule.rfqAlternateSkus.map(
          (e) => e.id,
        );
      }

      this.formData.patchValue({ alternateSkus: alternate_sku_ids });
    }

    this.loading = false;
    this.submitted = false;
  }

  private getAlternateSKU() :void {
    this.rfqSvc
      .getAlternateSKU({ id: this.rfq.id })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((res) => {
        let alternate_sku_ids :any = [];
        if (this.rfqConversionRule) {
          if (
            this.rfqConversionRule.rfqAlternateSkus &&
            this.rfqConversionRule.rfqAlternateSkus.length > 0
          ) {
            alternate_sku_ids = this.rfqConversionRule.rfqAlternateSkus.map(
              (e) => e.id,
            );
          }
        }

        this.alternateSKUs = res.filter(
          (alt) =>
            alt.unit_conversion_rule_id === null ||
            alternate_sku_ids.includes(alt.id),
        );
      });
  }

  initUnitOfMeasures() :void {
    if (this.rfq) {
      this.unitOfMeasures = this.rfq?.unit_of_measures;
      this.getBaseUnit();
    }
  }

  updateAlternateSkus() :void {
    const modalRef = this.modalSvc.open(UpdateAlternateSkusComponent, {});
    modalRef.componentInstance.rfq = this.rfq;
    modalRef.result.then(
      () => {
        this.cd.detectChanges();
      },
      () => {},
    );
  }

  getBaseUnit() :void {
    this.baseUnit = this.unitOfMeasures.find((uom) => uom.base_unit === 1);
  }

  cancel() :void {
    this.activeModal.close(null);
  }

  save() :void {
    this.loading = true;
    this.submitted = true;
    if (this.formData.invalid) {
      Swal.fire({
        title: 'Form Error!',
        text: 'Please fill all the required fields.',
        type: 'error',
        confirmButtonText: 'Back to Form',
      });

      this.loading = false;
      this.submitted = false;

      return;
    }

    const payload :any = Object.assign({}, this.formData.value);

    if (this.rfqConversionRule && this.rfqConversionRule.id) {
      this.unitConversionRuleService
        .update({ id: this.rfqConversionRule.id, ...payload })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(
          (resp) => {
            this.toastr.success('Rfq unit of conversion updated');
            this.loading = false;
            this.activeModal.close(resp);
          },
          (error) => {
            this.loading = false;
            this.toastr.error(
              'Error updating rfq unit of conversion. Please try again later or contact support.',
            );
          },
        );
    } else {
      this.unitConversionRuleService
        .create(payload)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(
          (resp) => {
            this.toastr.success('New rfq unit of conversion created');
            this.loading = false;
            this.activeModal.close(resp);
          },
          (error) => {
            this.loading = false;
            this.toastr.error(
              'Error creating rfq unit of conversion. Please try again later or contact support.',
            );
          },
        );
    }
  }
}
